/**
 * Domain model for Last Call (Red's edition).
 *
 * The app is the nightly count binder on a tablet. Its records match the rows and cells of
 * Red's Excel workbook ("Closing Liquor", "Beer Count Close", "Beer Order"), so a week can be
 * written back out to that workbook exactly. There is no server and no database service:
 * records live in this device's browser storage, and the weekly Excel file is the record that
 * gets kept.
 *
 * Conventions:
 * - Timestamps are ISO-8601 UTC strings. Business dates are `YYYY-MM-DD` in the venue's
 *   timezone after the business-day cutoff (a count at 2 AM belongs to the night before).
 * - Beer is counted in bottles/cans. Liquor is counted in ounces, exactly as written on the sheet.
 * - `null` means "not entered". Zero is a value. Never turn one into the other.
 */

export type ISODateTime = string;
export type BusinessDate = string;

export type Role = 'staff' | 'manager' | 'owner';

export interface Meta {
  id: string;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
  /** Goes up by one on every change. */
  version: number;
  /** Made-up practice records (practice mode only). */
  isSample?: boolean;
}

// ---------------------------------------------------------------------------
// The lists on the count sheets
// ---------------------------------------------------------------------------

/**
 * The four lists printed on Red's sheets:
 *  back_beer   "BACK BEER" — the first list on Beer Count Close
 *  front_beer  the second list on Beer Count Close (under the yellow bar): the front bar cooler
 *  liquor      the first list on Closing Liquor (up to the first TOTAL OZ)
 *  liqueur     the second list on Closing Liquor (up to the second TOTAL OZ)
 */
export type SectionId = 'back_beer' | 'front_beer' | 'liquor' | 'liqueur';
export const SECTIONS: SectionId[] = ['back_beer', 'front_beer', 'liquor', 'liqueur'];

export type Kind = 'beer' | 'liquor';

export const SECTION_KIND: Record<SectionId, Kind> = { back_beer: 'beer', front_beer: 'beer', liquor: 'liquor', liqueur: 'liquor' };

/** Something counted every night: a beer (in one or both beer lists) or a liquor. */
export interface Item extends Meta {
  name: string;
  kind: Kind;
  /** Which lists it's on, and its row position in each (1 = first row). */
  rows: Partial<Record<SectionId, number>>;
  active: boolean;
  /** Liquor sheet column B: the empty bottle's weight in oz (printed beside the name). */
  colB: number | string | null;
  /**
   * Liquor: the pour spout's weight in oz, on top of the empty bottle when it's weighed.
   * Absent = Red's usual metal spout (`DEFAULT_SPOUT_OZ`); null = this bottle has no spout.
   */
  spoutOz?: number | null;
  /**
   * A row kept empty on purpose (on Red's liquor sheet, between Triple Sec and Grand
   * Marnier). It's on the sheet but not counted; a manager can turn it into a drink.
   */
  blankRow?: boolean;
  /** Front bar beer: the amount it's normally stocked to, filled in with one tap. Null = count it every time. */
  usualFront: number | null;
  /** Beer Order sheet: bottles or cans in one case, and the par in cases. Both set = on the order sheet. */
  caseSize: number | null;
  parCases: number | null;
  /** Cost of one bottle or can before tax (liquor: one full bottle of `bottleMl`). */
  unitCost: number | null;
  /** Liquor bottle size in mL (750, 1140, 1750…). */
  bottleMl: number | null;
  /** What one drink sells for before tax. */
  drinkPrice: number | null;
  /** Liquor: one drink's pour in ounces. */
  servingOz: number | null;
}

// ---------------------------------------------------------------------------
// A night: one column of the sheets
// ---------------------------------------------------------------------------

/** The places counted at close, in walking order. `guns` = the two liquor gun meters. */
export type AreaId = 'back_beer' | 'front_beer' | 'liquor' | 'guns';
export const AREAS: AreaId[] = ['back_beer', 'front_beer', 'liquor', 'guns'];

export type NightStatus =
  /** Staff are counting. */
  | 'counting'
  /** Staff finished; waiting for a manager. */
  | 'finished'
  /** A manager approved it. Changing it means reopening it first. */
  | 'approved';

/** Numbers typed from the Micros report (the "1 BTL…" and "1OZ…" rows). */
export interface Micros {
  btl1: number | null;
  btl2: number | null;
  btl5: number | null;
  btlCooler: number | null;
  staffBeer: number | null;
  oz1: number | null;
  oz2: number | null;
  oz3: number | null;
  oz4: number | null;
  staffLiquor: number | null;
}

export const EMPTY_MICROS: Micros = { btl1: null, btl2: null, btl5: null, btlCooler: null, staffBeer: null, oz1: null, oz2: null, oz3: null, oz4: null, staffLiquor: null };

/**
 * Opening numbers typed by a manager. Normally they come from the night before (the sheet's
 * formulas do the same); they are needed for the very first night, or after a night that
 * wasn't counted. These are the yellow cells on Monday's column.
 */
export interface Openings {
  beer: number | null;
  liquor: number | null;
  gun1: number | null;
  gun2: number | null;
}

export const EMPTY_OPENINGS: Openings = { beer: null, liquor: null, gun1: null, gun2: null };

export interface Night extends Meta {
  /** Same as `date`. */
  id: BusinessDate;
  date: BusinessDate;
  status: NightStatus;
  /** Areas staff said they finished, with who and when. */
  areasDone: Partial<Record<AreaId, { by: string; at: ISODateTime }>>;
  /** Liquor gun meter readings at close. */
  gun1: number | null;
  gun2: number | null;
  micros: Micros;
  openings: Openings;
  /** "Count Initial" and "Recount Initial" rows. */
  countInitials: string;
  recountInitials: string;
  finishedBy: string | null;
  finishedAt: ISODateTime | null;
  approvedBy: string | null;
  approvedAt: ISODateTime | null;
  /** Items a manager looked at and said are right, with the numbers they saw (a later change shows it again). */
  okItems: Record<string, { by: string; at: ISODateTime; note: string; used: number | null; close: number | null }>;
  note: string;
}

export type CountSource = 'typed' | 'usual' | 'recount' | 'excel' | 'sample';

/** One cell of the sheet: an item's count in one list on one night. */
export interface CountEntry extends Meta {
  /** `${date}|${section}|${itemId}` */
  id: string;
  date: BusinessDate;
  section: SectionId;
  itemId: string;
  value: number | null;
  countedBy: string | null;
  countedAt: ISODateTime | null;
  source: CountSource;
  /** Set when a manager asked for this to be counted again. */
  recountRequestedAt: ISODateTime | null;
  recountRequestedBy: string | null;
  recountNote: string;
}

/**
 * What came in or went out during a night (the "Beer Added", "Transfered Out",
 * "Promo/Spillage" and "Liquor Added" rows). Recorded per item so the app can tell which
 * drink sold what; the sheet shows the night's total.
 */
export type MoveKind = 'added' | 'transfer_out' | 'promo_spill';

/** What kind of loss a promo/spillage entry was (all go in the sheet's "Promo/Spillage" row). */
export type LossReason = 'promo' | 'spilled' | 'broken' | 'other';

/** Where stock that came in came from (both go in the "Beer Added" / "Liquor Added" rows). */
export type CameFrom = 'downstairs' | 'delivery';

export interface Movement extends Meta {
  date: BusinessDate;
  kind: MoveKind;
  /** Beer or liquor (which sheet row it adds to). */
  sheet: Kind;
  /** Null only for a night total read from an Excel file that had no per-item detail. */
  itemId: string | null;
  /** Bottles/cans for beer; ounces for liquor. Always positive. */
  qty: number;
  /** Promo/spillage only: what happened. Absent on older entries and Excel night totals. */
  reason?: LossReason;
  /** Came in only: brought up from the basement, or straight from a delivery. */
  from?: CameFrom;
  /** Came in only: who carried it up (a person's id), when not the person who typed it in (`by`). */
  broughtBy?: string;
  /** Liquor brought up by the bottle: how many bottles of what size (the qty is the ounces). */
  bottles?: number;
  bottleOz?: number;
  note: string;
  by: string;
  at: ISODateTime;
  voided: boolean;
  voidedBy: string | null;
  voidedAt: ISODateTime | null;
}

// ---------------------------------------------------------------------------
// People, settings, planning, history
// ---------------------------------------------------------------------------

export interface Person extends Meta {
  name: string;
  /** Written in the "Count Initial" row. */
  initials: string;
  role: Role;
  active: boolean;
  /** Managers and owners can set a 4-digit PIN (stored as a hash, never as typed). */
  pinHash: string | null;
}

export interface CheckSettings {
  /** A beer is flagged when what went out differs from usual by at least this many bottles… */
  beerMinBottles: number;
  /** …a liquor by at least this many ounces… */
  liquorMinOz: number;
  /** …and by at least this share of the usual amount (0.5 = 50 %). */
  changePct: number;
  /** A count that went up with nothing recorded coming in is flagged from this many bottles… */
  beerIncrease: number;
  /** …or ounces. */
  liquorIncreaseOz: number;
}

export interface Settings {
  id: 'venue';
  venueName: string;
  timezone: string;
  /** "HH:mm". Anything before this time belongs to the night before. */
  cutoff: string;
  /**
   * "HH:mm", the day after a count: until then a manager can ask for a recount of that night
   * (and staff can do it). Never earlier than `cutoff`.
   */
  recountUntil: string;
  locale: string;
  checks: CheckSettings;
  /** Last time a week's Excel file was saved from this device. */
  lastExcelSavedAt: ISODateTime | null;
  lastExcelWeek: BusinessDate | null;
  updatedAt: ISODateTime;
  version: number;
}

/** Dates a manager expects to sell more or less (Dry January, a long weekend), with their own % guess. */
export interface PlanEvent extends Meta {
  name: string;
  startDate: BusinessDate;
  endDate: BusinessDate;
  /** Empty = everything. */
  categories: Kind[];
  /** −20 = 20 % less than usual. */
  changePct: number;
  note: string;
  active: boolean;
  createdBy: string;
}

/** A note from anyone using the app: a problem, an idea, or something confusing. */
export type FeedbackKind = 'problem' | 'idea' | 'confusing';
export type FeedbackStatus = 'new' | 'seen' | 'done';

export interface Feedback extends Meta {
  kind: FeedbackKind;
  text: string;
  /** The screen it was sent from, e.g. "/count/2026-10-26/liquor". */
  page: string;
  by: string;
  byName: string;
  at: ISODateTime;
  /** A short description of the device and browser, to help find device-only problems. */
  device: string;
  status: FeedbackStatus;
  /** What was done about it (written by whoever looks after the app). */
  reply: string;
}

export interface AuditEvent {
  id: string;
  at: ISODateTime;
  actorId: string;
  actorName: string;
  action: string;
  entity: string;
  entityId: string;
  summary: string;
  before: unknown;
  after: unknown;
  reason: string;
}
